import { useMemo } from 'react';
import moment from 'moment-timezone';
import {
  TIME_OPTIONS,
  parse12hrTime,
  formatTime12,
  findClosestTimeOption,
} from '../../fields/dateTime';

interface UseTimeOptionsArgs {
  selectedStartDate: Date | null;
  selectedStartTime: string;
  calendarBookedStartIso: string | null;
  /**
   * ISO of the booked slot's `end_time`. When the actual end falls
   * between two 15-min grid slots (e.g. 6-min interview ending at
   * 10:06), inject that exact time into `endTimeOptions` so the
   * `<Select>` can render it as the chosen value. Without this the
   * picker either auto-corrects away from the recruiter's value or we
   * have to display the next 15-min ceiling, which makes a 6-min
   * interview look like 15.
   */
  calendarBookedEndIso?: string | null;
  scheduleTz: string;
}

export interface TimeOptionsResult {
  /** Filtered start-time options — past times hidden when date is today. */
  startTimeOptions: typeof TIME_OPTIONS;
  /** Filtered end-time options — only times after the selected start.
   *  Includes the actual `bookedSlot.end` (time-of-day) when it falls
   *  off the 15-min grid, so the Select can render the recruiter's
   *  exact end without auto-correcting. */
  endTimeOptions: typeof TIME_OPTIONS;
  /** Minimum selectable date for the date picker. Advances to tomorrow
   *  when the selected start time is at or before the current wall clock. */
  datePickerMinDate: Date;
  /** The resolved effective start date (form value ?? calendar hint). */
  effectiveStartDate: Date | null;
  /** The resolved effective start time string (form value ?? calendar hint). */
  effectiveStartTimeStr: string;
}

export function useTimeOptions({
  selectedStartDate,
  selectedStartTime,
  calendarBookedStartIso,
  calendarBookedEndIso,
  scheduleTz,
}: UseTimeOptionsArgs): TimeOptionsResult {
  const calendarHintTime12 =
    calendarBookedStartIso !== null && calendarBookedStartIso !== ''
      ? findClosestTimeOption(formatTime12(calendarBookedStartIso, scheduleTz))
      : '';

  const effectiveStartTimeStr = selectedStartTime || calendarHintTime12 || '';

  const effectiveStartDate: Date | null =
    selectedStartDate ??
    (calendarBookedStartIso ? new Date(calendarBookedStartIso) : null);

  const isSelectedDateToday = effectiveStartDate
    ? moment(effectiveStartDate)
        .tz(scheduleTz)
        .isSame(moment().tz(scheduleTz), 'day')
    : false;

  const nowMins =
    moment().tz(scheduleTz).hours() * 60 + moment().tz(scheduleTz).minutes();

  const startTimeParsedForMinDate = effectiveStartTimeStr
    ? parse12hrTime(effectiveStartTimeStr)
    : null;
  const selectedTimeMins = startTimeParsedForMinDate
    ? startTimeParsedForMinDate.hours * 60 + startTimeParsedForMinDate.minutes
    : -1;
  const isWallClockStartTimePastOrEqualNow =
    selectedTimeMins >= 0 && selectedTimeMins <= nowMins;

  const datePickerMinDate = useMemo(
    () =>
      isWallClockStartTimePastOrEqualNow
        ? moment().tz(scheduleTz).add(1, 'day').startOf('day').toDate()
        : moment().tz(scheduleTz).startOf('day').toDate(),
    [isWallClockStartTimePastOrEqualNow, scheduleTz]
  );

  const startTimeOptions = isSelectedDateToday
    ? TIME_OPTIONS.filter((opt) => {
        const p = parse12hrTime(opt.value);
        return p ? p.hours * 60 + p.minutes > nowMins : false;
      })
    : TIME_OPTIONS;

  const startTimeParsed = effectiveStartTimeStr
    ? parse12hrTime(effectiveStartTimeStr)
    : null;
  const startTimeMins = startTimeParsed
    ? startTimeParsed.hours * 60 + startTimeParsed.minutes
    : -1;

  const baseEndTimeOptions =
    startTimeMins >= 0
      ? TIME_OPTIONS.filter((opt) => {
          const p = parse12hrTime(opt.value);
          return p ? p.hours * 60 + p.minutes > startTimeMins : false;
        })
      : TIME_OPTIONS;

  // Inject the recruiter's actual booked end as an extra option when it
  // falls between 15-min slots (e.g. 6-min interview → 10:06 AM). The
  // Select needs the exact value present in `options` to render it
  // without falling back to the first valid option, which is what was
  // silently turning a 6-min interview into 15 mins on edit + move.
  // Filtering rules match the base list: must be strictly after start
  // and not already on the 15-min grid.
  const endTimeOptions = (() => {
    if (!calendarBookedEndIso) return baseEndTimeOptions;
    const extraValue = formatTime12(calendarBookedEndIso, scheduleTz);
    if (!extraValue) return baseEndTimeOptions;
    const extraParsed = parse12hrTime(extraValue);
    if (!extraParsed) return baseEndTimeOptions;
    const extraMins = extraParsed.hours * 60 + extraParsed.minutes;
    if (startTimeMins >= 0 && extraMins <= startTimeMins) {
      return baseEndTimeOptions;
    }
    if (baseEndTimeOptions.some((opt) => opt.value === extraValue)) {
      return baseEndTimeOptions;
    }
    const extraOption = { value: extraValue, label: extraValue };
    const merged = [...baseEndTimeOptions, extraOption].sort((a, b) => {
      const pa = parse12hrTime(a.value);
      const pb = parse12hrTime(b.value);
      const ma = pa ? pa.hours * 60 + pa.minutes : 0;
      const mb = pb ? pb.hours * 60 + pb.minutes : 0;
      return ma - mb;
    });
    return merged;
  })();

  return {
    startTimeOptions,
    endTimeOptions,
    datePickerMinDate,
    effectiveStartDate,
    effectiveStartTimeStr,
  };
}
